// Global Instructions Rule Applied!

import { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { getAccessToken } from '../../../core/auth/authService'
import { store } from '../../../state_manager/store.ts'
import {
  fetchFundGroupSubscriptionsFiltered,
  fetchFundGroupSubscriptionsFilterOptions,
  type FundGroupSubscriptionsPaginatedResponse
} from '../services/fundGroupsService.ts'
import { useMenuItemUserPreference } from '../../global_configs/useMenuItemUserPreference.ts'
import { mergeUserPreferenceRowData } from '../../../state_manager/state_update_actions/global_configs/actions.ts'
import {
  areFilterValuesEqual,
  areSortConfigsEqual,
  getSubPreferenceRow,
  PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS,
  PREF_TABLE_COLUMN_KEYS,
  PREF_TABLE_FILTERS,
  PREF_TABLE_SORTING,
  resolveTableFiltersFromPreferenceRow,
  resolveTableSortingFromPreferenceRow,
  type FilterValue,
  type SortConfig
} from '../../global_configs/tableViewPreferences.ts'
import { MENU_ITEM_KEY_CONSOLIDATED_FUNDS } from '../../../state_manager/globals.ts'
import { fetchGlobalConfigsThunk } from '../../global_configs/controller.ts'
import type { AppDispatch, RootState } from '../../../state_manager/store.ts'
import { useFundGroupSubscriptionsReportId } from '../../../state_manager/hooks/useFundGroupSubscriptionsReportId.ts'
import { useReportingDefaultSorting } from '../../global_configs/useReportingDefaultSorting.ts'
import type { FundGroupSubscriptionRow } from '../../../state_manager/data_models/fund_group.ts'

const DEFAULT_SUBSCRIPTIONS_FILTERS: FilterValue[] = []

/**
 * useFundGroupSubscriptions — local-state hook for the consolidated-fund
 * Subscriptions grid. Mirrors useFundInvestors in shape (sub-key user
 * preferences under the consolidated-funds menu_item; local state for
 * active filters / sorting so multiple group tabs don't share).
 */
export function useFundGroupSubscriptions(fundGroupId: string | null | undefined) {
  const dispatch = useDispatch<AppDispatch>()
  const recordIdString = fundGroupId ? String(fundGroupId) : null

  const subscriptionsReportId = useFundGroupSubscriptionsReportId()
  // Consolidated Funds sits in two menu categories (Fund Management and
  // Operations). The saved grid preferences stay on the item the grid's report
  // belongs to, whichever copy the menu delivers first.
  const reportMenuItemId = useSelector(
    (state: RootState) =>
      (state.globalConfigs?.reporting_configs ?? []).find((r) => r.id === subscriptionsReportId)?.menu_item_id ?? null
  )
  const { menuItemId, preferenceRow, persistPreference } = useMenuItemUserPreference(
    MENU_ITEM_KEY_CONSOLIDATED_FUNDS,
    reportMenuItemId
  )

  const defaultTableSorting = useReportingDefaultSorting(subscriptionsReportId)

  const [data, setData] = useState<FundGroupSubscriptionRow[]>([])
  const [filterOptions, setFilterOptions] = useState<Record<string, Array<{ value: string; label: string }>>>({})
  const [activeFilters, setActiveFiltersLocal] = useState<FilterValue[]>(DEFAULT_SUBSCRIPTIONS_FILTERS)
  const [activeSorting, setActiveSortingLocal] = useState<SortConfig[]>([])
  const [hasMore, setHasMore] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [loading, setLoading] = useState(false)
  const [filterOptionsLoading, setFilterOptionsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [prefsReady, setPrefsReady] = useState(false)
  const skipPersistRef = useRef(true)

  // 1. Hydrate filters / sorting from prefs (under the subscriptions sub-key).
  useEffect(() => {
    const sub = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS)
    const nextFilters = resolveTableFiltersFromPreferenceRow(sub, DEFAULT_SUBSCRIPTIONS_FILTERS)
    const nextSorting = resolveTableSortingFromPreferenceRow(sub, defaultTableSorting)
    if (!areFilterValuesEqual(nextFilters, activeFilters)) setActiveFiltersLocal(nextFilters)
    if (!areSortConfigsEqual(nextSorting, activeSorting)) setActiveSortingLocal(nextSorting)
    setPrefsReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preferenceRow, defaultTableSorting])

  // 2. Fetch filter options once we have a group id.
  useEffect(() => {
    if (!recordIdString) {
      setFilterOptions({})
      return
    }
    let cancelled = false
    setFilterOptionsLoading(true)
    ;(async () => {
      const token = getAccessToken(store.getState().auth)
      const res = await fetchFundGroupSubscriptionsFilterOptions(recordIdString, token)
      if (!cancelled) {
        setFilterOptions(res.data || {})
        setFilterOptionsLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [recordIdString])

  // 3. Fetch data on filter/sort/group change (after prefs are ready).
  const fetchPage1 = useCallback(
    async (sorting: SortConfig[], filters: FilterValue[]) => {
      if (!recordIdString) return
      setLoading(true)
      setError(null)
      try {
        const token = getAccessToken(store.getState().auth)
        const res: FundGroupSubscriptionsPaginatedResponse = await fetchFundGroupSubscriptionsFiltered(
          recordIdString,
          sorting,
          filters,
          1,
          null,
          token
        )
        if (res.error) {
          setError(res.error)
          setData([])
          setHasMore(false)
        } else {
          setData(res.data)
          setHasMore(res.hasMore)
          setCurrentPage(1)
        }
      } finally {
        setLoading(false)
      }
    },
    [recordIdString]
  )

  useEffect(() => {
    if (!prefsReady || !recordIdString) return
    void fetchPage1(activeSorting, activeFilters)
  }, [prefsReady, recordIdString, activeFilters, activeSorting, fetchPage1])

  // 4. Persist filter/sort changes to user prefs (under the sub-key).
  const persistMergedSubPreference = useCallback(
    async (extra: Record<string, unknown> = {}) => {
      if (!menuItemId) return { success: false as const, error: 'No menu item' }
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const baseSubData = (baseData[PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS] as Record<string, unknown>) || {}
      const newSubData: Record<string, unknown> = {
        ...baseSubData,
        ...extra,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
      const merged: Record<string, unknown> = {
        ...baseData,
        [PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS]: newSubData
      }
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth.user
          if (user) {
            await fetchGlobalConfigsThunk(user)(dispatch)
          }
        }
      }
      return result
    },
    [menuItemId, activeFilters, activeSorting, persistPreference, dispatch]
  )

  useEffect(() => {
    if (!prefsReady || !menuItemId) return
    if (skipPersistRef.current) {
      skipPersistRef.current = false
      return
    }
    void persistMergedSubPreference().catch((err) => {
      if (process.env.NODE_ENV === 'development') {
        console.error('Failed to persist fund group subscription preferences:', err)
      }
    })
  }, [prefsReady, menuItemId, activeFilters, activeSorting, persistMergedSubPreference])

  // 5. Public handlers.
  const handleSetFilters = useCallback(
    (filters: FilterValue[]) => {
      if (areFilterValuesEqual(filters, activeFilters)) return
      setActiveFiltersLocal(filters)
    },
    [activeFilters]
  )

  const handleSetSorting = useCallback(
    (sorting: SortConfig[]) => {
      if (areSortConfigsEqual(sorting, activeSorting)) return
      setActiveSortingLocal(sorting)
    },
    [activeSorting]
  )

  const handleResetFilters = useCallback(() => {
    if (!menuItemId) {
      setActiveFiltersLocal(DEFAULT_SUBSCRIPTIONS_FILTERS)
      setActiveSortingLocal(defaultTableSorting)
      return
    }
    void (async () => {
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const baseData = (row?.data ?? {}) as Record<string, unknown>
      const merged = { ...baseData }
      delete (merged as any)[PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS]
      const result = await persistPreference(undefined, merged)
      if (result.success) {
        dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        skipPersistRef.current = true
        setActiveFiltersLocal(DEFAULT_SUBSCRIPTIONS_FILTERS)
        setActiveSortingLocal(defaultTableSorting)
      }
    })()
  }, [menuItemId, defaultTableSorting, persistPreference, dispatch])

  const persistColumnFieldOrder = useCallback(
    async (fieldNames: string[]) => {
      return persistMergedSubPreference({ [PREF_TABLE_COLUMN_KEYS]: fieldNames })
    },
    [persistMergedSubPreference]
  )

  const resetColumnLayoutToDefault = useCallback(async () => {
    if (!menuItemId) return { success: false as const, error: 'No menu item' }
    const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
    const row = reports.find((p: any) => p.menu_item_id === menuItemId)
    const baseData = (row?.data ?? {}) as Record<string, unknown>
    const baseSubData = ((baseData[PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS] as Record<string, unknown>) || {})
    const newSubData = { ...baseSubData }
    delete (newSubData as any)[PREF_TABLE_COLUMN_KEYS]
    const merged: Record<string, unknown> = {
      ...baseData,
      [PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS]: {
        ...newSubData,
        [PREF_TABLE_FILTERS]: activeFilters,
        [PREF_TABLE_SORTING]: activeSorting
      }
    }
    const result = await persistPreference(undefined, merged)
    if (result.success) {
      dispatch(mergeUserPreferenceRowData(menuItemId, merged))
    }
    return result
  }, [menuItemId, activeFilters, activeSorting, persistPreference, dispatch])

  const refetch = useCallback(
    () => fetchPage1(activeSorting, activeFilters),
    [fetchPage1, activeSorting, activeFilters]
  )

  const subPreferenceRow = getSubPreferenceRow(preferenceRow, PREF_SUBKEY_FUND_GROUP_SUBSCRIPTIONS)

  return {
    data,
    loading,
    error,
    filterOptions,
    filterOptionsLoading,
    activeFilters,
    activeSorting,
    hasMore,
    currentPage,
    preferenceRow: subPreferenceRow,
    handleSetFilters,
    handleSetSorting,
    handleResetFilters,
    persistColumnFieldOrder,
    resetColumnLayoutToDefault,
    refetch,
    defaultTableFilters: DEFAULT_SUBSCRIPTIONS_FILTERS,
    defaultTableSorting
  }
}
